import {
  Box,
  Button,
  ButtonProps,
  Chip,
  Stack,
  Typography,
} from '@mui/material'
import isNumber from 'lodash/isNumber'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import SwiperCore from 'swiper'
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'
import {
  CautionText,
  Divider,
  IconButton as UIIconButton,
  Icons,
  InformationModal,
  ItemImageArea,
} from 'utoniq-ui'

import {
  ItemType,
  Maybe,
  SkuTagFragment,
  UserItemDetailBoxFragment,
} from '@/clients/utoniq-core/schema'
import { ThroughTicketTag } from '@/components/organisms'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Cell, CellFrame, Organism, OrganismFrame } from '@/core/architecture'
import {
  individualHiddenItemInfo,
  individualHideItemIssuedNumber,
  individualHideStoreItemInfo,
  individualHideStoreSupplementLink,
} from '@/core/individual'
import { filterHiddenItem } from '@/lib/array'

type ItemDetailBoxProps = {
  hidden?: {
    trade?: boolean
    itemInfo?: boolean
    expansion?: boolean
  }
  showPreSale?: boolean
  skuUnit?: SkuTagFragment
  userId?: string
  isPremium?: boolean
  purchasablePlanNames?: string[]
  formattedSerialNumber?: string
  hiddenSupplementLink?: boolean
  hiddenInfo?: boolean
  hiddenPurchaseLimit?: boolean
  purchaseLimitByUser?: number | null
  userItem?: Maybe<UserItemDetailBoxFragment>
  viewable: boolean
  isPickUp?: boolean
  isMerch?: boolean
  onClickSerial?: () => Promise<void>
  onClickExpansion?: () => Promise<void>
  onClickTrade?: () => Promise<void>
  onClickNftIcon?: () => void
  onClickShare?: () => void
}

type SupplementLinkSetCellProps = Pick<
  ItemDetailBoxProps,
  | 'onClickSerial'
  | 'onClickExpansion'
  | 'onClickTrade'
  | 'onClickShare'
  | 'hidden'
  | 'userId'
  | 'viewable'
  | 'userItem'
  | 'purchaseLimitByUser'
> & {
  itemId: string
  artistId: string
  disabledTrade: boolean
  commentCount?: number
}

export const ItemDetailBox: Organism<ItemDetailBoxProps> = ({
  userItem,
  userId,
  viewable,
  showPreSale,
  purchasablePlanNames,
  isPremium,
  formattedSerialNumber,
  hidden,
  hiddenSupplementLink,
  hiddenInfo,
  onClickSerial,
  onClickExpansion,
  onClickTrade,
  onClickNftIcon,
  onClickShare,
  hiddenPurchaseLimit = true,
  skuUnit,
  isPickUp,
  isMerch,
  purchaseLimitByUser,
}) => {
  const { analytics } = useAnalytics()
  const { t } = useTranslation('userItemDetail')
  const swiperRef = useRef<SwiperCore | null>(null)
  const { generatePath, routerPages } = useRouter<{ itemId: string }>()
  const [index, setIndex] = useState<number>(0)
  const location = useLocation()

  const disabledTrade = useMemo<boolean>(
    () =>
      !!(
        userItem?.isUserProtected ||
        userItem?.isSystemProtected ||
        (userItem?.item.type === ItemType.Merch &&
          userItem?.serials?.filter(serial => !serial.isProtected))
      ),
    [userItem?.isUserProtected, userItem?.isSystemProtected]
  )
  const isNft = !!userItem?.item?.nft?.chains

  const itemIcon = useMemo(() => {
    switch (userItem?.item?.type) {
      case ItemType.Audio:
        return <Icons.Audio fontSize="xSmall" />
      case ItemType.Video:
        return <Icons.Video fontSize="xSmall" />
      case ItemType.Magazine:
        return <Icons.Magazine fontSize="xSmall" />
      case ItemType.Merch:
        return <Icons.Merch fontSize="xSmall" />
      case ItemType.Wallpaper:
        return <Icons.Wallpaper fontSize="xSmall" />
      case ItemType.Ticket:
        return <Icons.Ticket fontSize="xSmall" />
      default:
        return null
    }
  }, [userItem?.item?.type])

  const info = useMemo<
    {
      label: string
      value: string | number | JSX.Element
      hidden?: boolean
    }[]
  >(
    () => [
      {
        label: t('rarity'),
        value: userItem?.item?.rarity?.name ?? '-',
        hidden: (userItem?.item?.rarity?.name ?? '-') === '-',
      },
      {
        label: t('issueNumber'),
        value: userItem?.item?.issuedNumber ?? '∞',
        hidden: hiddenInfo || individualHideItemIssuedNumber(),
      },
      {
        label: t(
          userItem?.item?.price && userItem.item.price > 0
            ? 'purchaseLimit'
            : 'acquisitionLimit'
        ),
        value: purchaseLimitByUser ?? userItem?.item?.purchaseLimitByUser ?? 0,
        hidden:
          hiddenPurchaseLimit ||
          !isNumber(userItem?.item?.purchaseLimitByUser) ||
          userItem?.item?.purchaseLimitByUser === 0 ||
          !isNumber(userItem?.item?.price),
      },
      {
        label: t('possessionsNumber'),
        value: userItem?.number ?? 0,
        hidden: (userItem?.number ?? 0) === 0,
      },
      {
        label: t('type'),
        value: (
          <Stack direction="row">
            {itemIcon}&nbsp;&nbsp;
            {t(userItem?.item?.type || '')}
          </Stack>
        ),
      },
    ],
    [
      t,
      userItem?.item?.rarity?.name,
      userItem?.item?.issuedNumber,
      userItem?.item?.price,
      userItem?.item?.purchaseLimitByUser,
      userItem?.item?.type,
      userItem?.number,
      hiddenInfo,
      hiddenPurchaseLimit,
      itemIcon,
    ]
  )

  const images = useMemo<{ src: string; caption?: string | null }[]>(() => {
    const imageArr =
      userItem?.item?.images?.map(({ mediumUri, caption }) => ({
        src: mediumUri || '',
        caption,
      })) ?? []
    if (skuUnit?.thumbnail?.mediumUri) {
      imageArr.unshift({
        src: skuUnit.thumbnail.mediumUri,
        caption: '',
      })
    } else if (userItem?.item?.thumbnail?.front?.mediumUri) {
      imageArr.unshift({
        src: userItem.item.thumbnail.front.mediumUri,
        caption: '',
      })
    }
    return imageArr.filter(({ src }) => !!src)
  }, [
    skuUnit?.thumbnail?.mediumUri,
    userItem?.item?.images,
    userItem?.item?.thumbnail?.front?.mediumUri,
  ])

  // set index = 0 when select sku
  useEffect(() => {
    if (skuUnit?.thumbnail?.mediumUri) {
      setIndex(0)
      swiperRef.current?.slideTo(1)
    }
  }, [skuUnit?.thumbnail?.mediumUri])

  // キャッシュが残っていた場合indexがリセットされないため強制的に戻す
  useEffect(() => {
    swiperRef.current?.slideTo(0)
    swiperRef.current?.update()
  }, [location.pathname])

  return (
    <OrganismFrame sx={{ py: 2 }}>
      <Stack
        alignItems="center"
        sx={{
          position: 'relative',
          mx: -5,
          //fixed swiper scroll
          '.swiper': { maxWidth: '100%' },
        }}
      >
        {images.length > 1 && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              bgcolor: 'grey.900',
              borderRadius: '40px',
              p: '3px 6px',
              zIndex: 2,
            }}
          >
            <Typography variant="subtitle2" color="white">
              {index + 1}/{images.length}
            </Typography>
          </Box>
        )}
        {images.length > 1 && (
          <>
            <NextArrow
              disabled={index + 1 === images.length}
              onClick={() => swiperRef.current?.slideNext()}
            />
            <PrevArrow
              disabled={!index}
              onClick={() => swiperRef.current?.slidePrev()}
            />
          </>
        )}
        <Swiper
          loop={false}
          slidesPerView={1}
          centeredSlides
          enabled
          spaceBetween={0}
          initialSlide={0}
          onActiveIndexChange={(swiper: SwiperClass) =>
            setIndex(swiper.activeIndex)
          }
          onSwiper={(swiper: SwiperCore) => (swiperRef.current = swiper)}
        >
          {images.map(({ src, caption }, index) => (
            <SwiperSlide key={index}>
              <Stack
                flex={1}
                alignItems="center"
                justifyContent="center"
                sx={{
                  button: { height: 375 },
                  position: 'relative',
                  mb: 5,
                }}
              >
                <ItemImageArea
                  src={src}
                  size="large"
                  noItem={!viewable}
                  fullWidth
                />
                {!!caption && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      px: 2,
                      py: '2px',
                      bgcolor: 'rgba(32, 52, 63, 0.6)',
                      borderRadius: 1,
                    }}
                  >
                    <Typography component="p" variant="caption" color="white">
                      {caption}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </SwiperSlide>
          ))}
        </Swiper>
        {!!userItem?.item?.purchasableUsers?.length && (
          <Stack
            direction="row"
            alignItems="center"
            sx={{ mt: 5, gap: 1, px: 5, width: '100%' }}
          >
            <Icons.MembersOnly fontSize="small" />
            <Typography color="text.hint" variant="subtitle2">
              {t('forLimitedUser', '対象者限定')}
            </Typography>
          </Stack>
        )}
        {isPremium && purchasablePlanNames && purchasablePlanNames.length > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            sx={{ mb: 2, gap: 1, px: 5, width: '100%' }}
          >
            <Icons.MembersOnly fontSize="small" sx={{ color: 'info.main' }} />
            <Typography color="text.hint" variant="subtitle2">
              {purchasablePlanNames.join(', ')}
            </Typography>
          </Stack>
        )}
        <Stack
          direction="row"
          flex={1}
          alignItems="center"
          sx={{ px: 5, width: '100%' }}
        >
          {isNft && <UIIconButton.Nft onClick={onClickNftIcon} />}
          <Typography variant="h3">{userItem?.item?.title}</Typography>
        </Stack>
        {userItem?.item?.metadata?.ticket?.isThroughTicket && (
          <Stack width="100%" px={5}>
            <ThroughTicketTag />
          </Stack>
        )}
        <NoticeCell isEventPickup={!!isPickUp} isMerch={!!isMerch} />
        {showPreSale && (
          <Stack sx={{ pt: 5, px: 5, width: '100%' }}>
            <CautionText
              type="error"
              sx={{
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: 'grey.50',
                border: 'none',
                minHeight: 44,
              }}
            >
              {t(
                'availableFoPreSaleNote',
                'この商品は先行販売で購入できます。'
              )}
            </CautionText>
          </Stack>
        )}
        {formattedSerialNumber && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={3}
            sx={{ pt: 4, px: 5, width: '100%' }}
          >
            <Typography variant="overline" color="text.hint">
              {t('serialNumber')}
            </Typography>
            <Chip
              label={
                <Typography variant="overline">
                  {formattedSerialNumber}
                </Typography>
              }
              size="small"
              sx={{ minWidth: 44, bgcolor: 'grey.200' }}
            />
          </Stack>
        )}
        {!!(
          userItem?.item?.description ??
          userItem?.item?.metadata?.ticket?.description
        ) && (
          <Typography variant="body2" sx={{ pt: 5, px: 5, width: '100%' }}>
            {userItem?.item?.description ??
              userItem?.item?.metadata?.ticket?.description}
          </Typography>
        )}
      </Stack>
      {!individualHideStoreItemInfo() && (
        <Stack direction="row" justifyContent="space-between" sx={{ pt: 3 }}>
          <Stack direction="row">
            {filterHiddenItem(info).map(({ label, value }, index) => (
              <Stack direction="row" key={index}>
                <Divider orientation="vertical" />
                <Stack sx={{ px: 3 }}>
                  <Typography
                    variant="overline"
                    color="text.hint"
                    sx={{ py: 2 }}
                  >
                    {label}
                  </Typography>
                  <Typography variant="overline">{value}</Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
          {hiddenSupplementLink && userItem?.item.id && (
            <Button
              variant="text"
              sx={{
                p: 1,
                borderRadius: 0,
                minWidth: 44,
                alignSelf: 'stretch',
                display: 'flex',
                height: 'unset',
                color: 'text.secondary',
              }}
              onClick={() => {
                analytics?.track('click_comment', {
                  applicationKey: config.applicationKey,
                  artistId: userItem?.item?.issuedArtist?.id,
                  name: 'store_detail',
                })
              }}
              href={generatePath(routerPages.ItemComment, {
                itemId: userItem?.item?.id,
              })}
            >
              <Icons.Reply sx={{ color: 'grey.600' }} />
              <Typography variant="body2" sx={{ pl: 1, color: 'inherit' }}>
                {userItem?.item?.commentCount}
              </Typography>
            </Button>
          )}
        </Stack>
      )}
      {!hiddenSupplementLink &&
        userItem?.item.id &&
        !individualHideStoreSupplementLink() && (
          <SupplementLinkSetCell
            itemId={userItem.item.id}
            artistId={userItem?.item?.issuedArtist?.id ?? ''}
            viewable={viewable}
            disabledTrade={disabledTrade}
            commentCount={userItem.item.commentCount}
            userId={userId}
            hidden={hidden}
            onClickSerial={onClickSerial}
            onClickExpansion={onClickExpansion}
            onClickTrade={onClickTrade}
            userItem={userItem}
            onClickShare={onClickShare}
          />
        )}
    </OrganismFrame>
  )
}

const SupplementLinkSetCell: Cell<SupplementLinkSetCellProps> = ({
  itemId,
  artistId,
  userId,
  commentCount,
  disabledTrade,
  hidden,
  viewable,
  onClickSerial,
  onClickExpansion,
  userItem,
  onClickTrade,
  onClickShare,
}) => {
  const { t } = useTranslation('userItemDetail')
  const { analytics } = useAnalytics()
  const { generatePath, routerPages } = useRouter<{ itemId: string }>()
  const [openNotTradeConfirm, setOpenNotTradeConfirm] = useState<boolean>(false)
  const supplements = [
    {
      title: 'expansion',
      hidden: hidden?.expansion,
      icon: <Icons.Expansion sx={{ mx: 'auto', color: 'inherit' }} />,
      onClick: onClickExpansion,
      disabled: !viewable,
    },
    {
      title: 'itemInfo',
      hidden: hidden?.itemInfo || individualHiddenItemInfo(),
      icon: <Icons.ItemInfo sx={{ mx: 'auto', color: 'inherit' }} />,
      onClick: onClickSerial,
      href: onClickSerial
        ? undefined
        : generatePath(
            userId
              ? routerPages.UserItemInformation
              : routerPages.OwnedItemInformation,
            {
              itemId,
              userId,
            }
          ),
      disabled: false,
    },
    {
      title: 'share',
      hidden: !onClickShare,
      icon: <Icons.Share sx={{ mx: 'auto', color: 'inherit' }} />,
      onClick: onClickShare,
    },
    {
      title: 'trade',
      hidden: hidden?.trade || !config.modules.trade,
      icon: (
        <Icons.TradeRequest
          sx={{
            mx: 'auto',
            color: disabledTrade ? 'text.disabled' : 'inherit',
          }}
        />
      ),
      onClick: disabledTrade
        ? () => setOpenNotTradeConfirm(true)
        : onClickTrade,
      disabled: disabledTrade,
    },
  ]
  return (
    <CellFrame sx={{ py: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ py: 2 }}
      >
        <Stack direction="row">
          {filterHiddenItem(supplements).map(
            ({ title, icon, onClick, disabled, href }, index) => (
              <Button
                key={index}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  p: 1,
                  color: 'text.primary',
                  borderRadius: 0,
                  textAlign: 'center',
                  '&.Mui-disabled': {
                    background: 'inherit',
                    color: 'text.disabled',
                  },
                }}
                href={href}
                onClick={onClick}
                variant="text"
                disabled={disabled}
              >
                {icon}
                <Typography
                  variant="overline"
                  color={disabled ? 'text.disabled' : 'text.hint'}
                  sx={{ pt: 1 }}
                >
                  {t(title)}
                </Typography>
              </Button>
            )
          )}
        </Stack>
        <Button
          variant="text"
          sx={{
            p: 1,
            borderRadius: 0,
            minWidth: 44,
            alignSelf: 'stretch',
            display: 'flex',
            height: 'unset',
            color: 'text.secondary',
          }}
          onClick={() => {
            analytics?.track('click_comment', {
              applicationKey: config.applicationKey,
              artistId,
              name: 'store_detail',
            })
          }}
          href={generatePath(routerPages.ItemComment, { itemId })}
        >
          <Icons.Reply sx={{ color: 'grey.600' }} />
          <Typography variant="body2" sx={{ pl: 1, color: 'inherit' }}>
            {commentCount}
          </Typography>
        </Button>
      </Stack>
      <InformationModal
        open={openNotTradeConfirm}
        onClose={() => setOpenNotTradeConfirm(false)}
        title={t('notTradeConfirmTitle')}
        content={t(
          userItem?.item.sku ? 'notTradeConfirmSKUDesc' : 'notTradeConfirmDesc'
        )}
        buttons={
          <Button
            color="primary"
            sx={{ minWidth: 212 }}
            size="large"
            onClick={() => setOpenNotTradeConfirm(false)}
          >
            OK
          </Button>
        }
      />
    </CellFrame>
  )
}

const NextArrow: FC<ButtonProps> = props => (
  <UIIconButton.NextArrow
    size="large"
    sx={{
      bgcolor: 'transparent',
      position: 'absolute',
      top: (375 - 52) / 2,
      right: 8,
      zIndex: 2,
      svg: {
        color: 'grey.400',
        fontSize: 27,
      },
    }}
    {...props}
  />
)

const PrevArrow: FC<ButtonProps> = props => (
  <UIIconButton.PrevArrow
    size="large"
    sx={{
      bgcolor: 'transparent',
      position: 'absolute',
      top: (375 - 52) / 2,
      left: 8,
      zIndex: 2,
      svg: {
        color: 'grey.400',
        fontSize: 27,
      },
    }}
    {...props}
  />
)

const NoticeCell = ({
  isEventPickup,
  isMerch,
}: {
  isEventPickup: boolean
  isMerch: boolean
}) => {
  const { t } = useTranslation('storeItemDetail')

  if (!(isEventPickup || isMerch)) return null

  return (
    <Box sx={{ width: '100%', px: 5, mt: 4 }}>
      <Box
        sx={{
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'grey.50',
          py: 3,

          mb: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Icons.Error color="error" />
          <Typography variant="caption">
            {isMerch
              ? t('isMerch', 'この商品は配送される商品です')
              : t('isPickupItem', 'この商品は会場での受け取りが必要な商品です')}
          </Typography>
        </Stack>
      </Box>
    </Box>
  )
}
